import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Car,
  Plus,
  Grid3x3,
  List,
  MapPin,
  Users,
  Fuel,
  AlertCircle,
  Tag,
  Upload,
  Download,
  Sparkles,
  EyeOff
} from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import RefreshButton from '../../components/ui/RefreshButton';
import Card, { CardContent } from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/Table';
import { formatName, cldImg, formatHeadlineRate } from '../../lib/utils';
import { carAPI } from '../../services/api';
import CarLoader from '../../components/ui/CarLoader';
import { useAuth } from '../../context/AuthContext';
import BulkImportModal from '../../components/Cars/BulkImportModal';
import { toCSV, downloadFile } from '../../lib/csv';

// Export columns mirror the Rental / Sale import templates so a downloaded file
// can be edited and re-uploaded straight back in.
const COMMON_COLS = ['registrationNumber', 'make', 'model', 'year', 'vehicleType', 'fuelType', 'transmission', 'seats', 'color', 'mileage'];
const LOC_COLS = ['city', 'state', 'description', 'latitude', 'longitude', 'imageUrls'];
const RENTAL_EXPORT_HEADERS = [...COMMON_COLS, 'pricePerDay', 'pricePerHour', 'pricePerKm', 'minBookingDays', ...LOC_COLS];
const SALE_EXPORT_HEADERS = [...COMMON_COLS, 'salePrice', 'salePriceType', 'kmDriven', 'noOfOwners', 'condition', 'insuranceValidTill', 'registrationYear', 'accidentFree', 'rcAvailable', ...LOC_COLS];

const yn = (v) => (v ? 'yes' : 'no');
const ymd = (d) => { if (!d) return ''; const dt = new Date(d); return isNaN(dt.getTime()) ? '' : dt.toISOString().slice(0, 10); };

// One row object with every possible field; toCSV picks the columns per template.
const carToRow = (c) => ({
  registrationNumber: c.registrationNumber || '',
  make: c.make || '',
  model: c.model || '',
  year: c.year || '',
  vehicleType: c.vehicleType || '',
  fuelType: c.fuelType || '',
  transmission: c.transmission || '',
  seats: c.seats || '',
  color: c.color || '',
  mileage: c.mileage || '',
  // rental
  pricePerDay: c.pricePerDay || '',
  pricePerHour: c.pricePerHour || '',
  pricePerKm: c.pricePerKm || '',
  minBookingDays: c.minBookingDays || '',
  // sale
  salePrice: c.salePrice || '',
  salePriceType: c.salePriceType || '',
  kmDriven: c.saleDetails?.kmDriven ?? '',
  noOfOwners: c.saleDetails?.noOfOwners ?? '',
  condition: c.saleDetails?.condition || '',
  insuranceValidTill: ymd(c.saleDetails?.insuranceValidTill),
  registrationYear: c.saleDetails?.registrationYear ?? '',
  accidentFree: c.saleDetails?.accidentFree === undefined ? '' : yn(c.saleDetails.accidentFree),
  rcAvailable: c.saleDetails?.rcAvailable === undefined ? '' : yn(c.saleDetails.rcAvailable),
  // location + photos
  city: c.location?.city || '',
  state: c.location?.state || '',
  description: c.description || '',
  latitude: c.location?.coordinates?.latitude ?? '',
  longitude: c.location?.coordinates?.longitude ?? '',
  imageUrls: Array.isArray(c.images) ? c.images.join('|') : '',
});

const EXPORTS = {
  rental: { headers: RENTAL_EXPORT_HEADERS, filter: (c) => c.forRent !== false, file: 'rental-vehicles' },
  sale: { headers: SALE_EXPORT_HEADERS, filter: (c) => !!c.forSale, file: 'sale-vehicles' },
};

const CarList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cars, setCars] = useState([]);
  const [filteredCars, setFilteredCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showImport, setShowImport] = useState(false);

  const [exportOpen, setExportOpen] = useState(false);

  // Export as CSV, split by purpose so it matches the Rental / Sale templates.
  const handleExport = (mode) => {
    setExportOpen(false);
    const cfg = EXPORTS[mode];
    const source = (Array.isArray(cars) ? cars : []).filter(cfg.filter);
    if (!source.length) return;
    const csv = toCSV(cfg.headers, source.map(carToRow));
    const stamp = new Date().toISOString().slice(0, 10);
    downloadFile(csv, `${cfg.file}-${stamp}.csv`);
  };
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const businessType = user?.businessType || 'both';
  // Always start on 'all'. An owner's businessType is a preference, not a fact
  // about their fleet — seeding this from it hid vehicles they actually own.
  const [purposeFilter, setPurposeFilter] = useState('all'); // all | rental | sale
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const carsPerPage = 12;

  // Check verification status for owners
  const isAdmin = user?.role === 'admin';
  const isOwner = user?.role === 'owner';
  const verificationStatus = user?.verificationStatus;
  const isVerified = verificationStatus === 'verified';

  // Subscription cap. planUsage comes from the API (the backend is the source of truth);
  // limit === null means unlimited.
  const [planUsage, setPlanUsage] = useState(null);
  const planLimit = planUsage?.limit ?? null;
  const planUsed = planUsage?.used ?? 0;
  const atPlanLimit = planLimit !== null && planUsed >= planLimit;
  const hiddenCount = cars.filter((c) => c.planHidden).length;

  // Offer the tabs whenever the fleet genuinely holds both kinds, so a mislabelled
  // owner can still reach every vehicle.
  const rentalCount = cars.filter((c) => c.forRent !== false).length;
  const saleCount = cars.filter((c) => !!c.forSale).length;
  const showPurposeTabs = rentalCount > 0 && saleCount > 0;

  const canAddVehicle = (!isOwner || isVerified) && !atPlanLimit;
  const addBlockedReason = !isVerified && isOwner
    ? 'Account verification required to add vehicles'
    : atPlanLimit
      ? `Your ${planUsage?.plan} plan allows ${planLimit} vehicles — upgrade to add more`
      : '';

  useEffect(() => {
    fetchCars();
  }, []);

  useEffect(() => {
    filterCars();
  }, [cars, searchTerm, statusFilter, purposeFilter, ownerFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCars = async () => {
    try {
      setLoading(true);
      const response = await carAPI.getAllCars();
      const carsData = response.data.cars || response.data || [];
      setCars(carsData);
      setPlanUsage(response.data.planUsage || null);
    } catch (error) {
      console.error('Error fetching cars:', error);
    } finally {
      setLoading(false);
    }
  };

  // Toggle public listing for a car
  const togglePublicListing = async (e, carId, currentValue) => {
    e.stopPropagation();
    try {
      await carAPI.updateCar(carId, { isPublicListing: !currentValue });
      setCars(prev => prev.map(c => c._id === carId ? { ...c, isPublicListing: !currentValue } : c));
    } catch (error) {
      console.error('Error toggling public listing:', error);
    }
  };

  // Derive effective status from car data
  const getCarStatus = (car) => {
    if (car.status === 'maintenance') return 'maintenance';
    if (car.status === 'inactive') return 'inactive';
    if (car.status === 'booked' || car.isAvailable === false) return 'booked';
    return 'available'; // default: active, available, or no status
  };

  // Get unique owners for admin filter
  const uniqueOwners = isAdmin ? [...new Map(cars.filter(c => c.ownerId && typeof c.ownerId === 'object').map(c => [
    c.ownerId._id,
    { id: c.ownerId._id, name: c.ownerId.name || 'Unknown', travelsName: c.ownerId.travelsInfo?.name || '' }
  ])).values()] : [];

  const filterCars = () => {
    let filtered = [...cars];

    // Owner filter (admin only)
    if (isAdmin && ownerFilter !== 'all') {
      filtered = filtered.filter(car => {
        const oid = car.ownerId?._id || car.ownerId;
        return oid === ownerFilter || oid?.toString() === ownerFilter;
      });
    }

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(car =>
        (car.make || '').toLowerCase().includes(search) ||
        (car.model || '').toLowerCase().includes(search) ||
        (car.registrationNumber || '').toLowerCase().includes(search) ||
        (car.fuelType || '').toLowerCase().includes(search) ||
        (typeof car.location === 'object'
          ? (car.location?.city || '').toLowerCase().includes(search)
          : (car.location || '').toLowerCase().includes(search))
      );
    }

    // Purpose filter — rental vs buy/sell
    if (purposeFilter === 'rental') {
      filtered = filtered.filter(car => car.forRent !== false);
    } else if (purposeFilter === 'sale') {
      filtered = filtered.filter(car => !!car.forSale);
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(car => getCarStatus(car) === statusFilter);
    }

    setFilteredCars(filtered);
    setCurrentPage(1);
  };

  const getStatusBadge = (car) => {
    const status = getCarStatus(car);
    const config = {
      booked: { variant: 'warning', label: 'Booked' },
      maintenance: { variant: 'secondary', label: 'Maintenance' },
      inactive: { variant: 'danger', label: 'Inactive' },
      available: { variant: 'success', label: 'Available' }
    };
    const { variant, label } = config[status] || config.available;
    return <Badge variant={variant} className="text-xs">{label}</Badge>;
  };

  // Pagination
  const totalPages = Math.ceil(filteredCars.length / carsPerPage);
  const paginatedCars = filteredCars.slice((currentPage - 1) * carsPerPage, currentPage * carsPerPage);

  const PaginationUI = () => filteredCars.length > carsPerPage ? (
    <div className="flex items-center justify-between mt-4 bg-white shadow rounded-lg px-4 py-3">
      <p className="text-xs text-gray-500">
        Showing {((currentPage - 1) * carsPerPage) + 1}-{Math.min(currentPage * carsPerPage, filteredCars.length)} of {filteredCars.length}
      </p>
      <div className="flex items-center gap-1">
        <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
          className="px-2.5 py-1 rounded-lg border text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40">Prev</button>
        {Array.from({ length: totalPages }, (_, i) => i + 1)
          .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
          .map((page, idx, arr) => (
            <React.Fragment key={page}>
              {idx > 0 && arr[idx - 1] !== page - 1 && <span className="px-1 text-gray-400 text-xs">...</span>}
              <button onClick={() => setCurrentPage(page)}
                className={`px-2.5 py-1 rounded-lg text-xs font-medium ${currentPage === page ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>{page}</button>
            </React.Fragment>
          ))}
        <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
          className="px-2.5 py-1 rounded-lg border text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40">Next</button>
      </div>
    </div>
  ) : null;

  const CarGridView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
      {paginatedCars.map((car) => (
        <Card
          key={car._id}
          className="hover:shadow-md transition-shadow overflow-hidden cursor-pointer"
          onClick={() => navigate(`/cars/${car._id}`)}
        >
          <div className="relative h-32 bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
            {car.images && car.images.length > 0 ? (
              <img src={cldImg(car.images[0], { w: 400 })} loading="lazy" alt={`${formatName(car.make)} ${formatName(car.model)}`} className={`w-full h-full object-cover ${car.planHidden ? 'grayscale opacity-60' : ''}`} />
            ) : (
              <Car className="h-16 w-16 text-blue-600" />
            )}
            {car.planHidden && (
              <span className="absolute top-2 left-2 inline-flex items-center gap-1 bg-amber-500 text-white text-[10px] font-bold px-2 py-1 rounded-full shadow-sm">
                <EyeOff className="h-3 w-3" /> Hidden — upgrade
              </span>
            )}
          </div>
          <CardContent className="p-3">
            <div className="flex items-start justify-between mb-1">
              <div>
                <h3 className="text-sm font-bold text-gray-900">
                  {formatName(car.make)} {formatName(car.model)}
                </h3>
                <p className="text-xs text-gray-500">{car.year}</p>
                {isAdmin && car.ownerId?.name && (
                  <p className="text-[10px] text-blue-600 font-medium">{car.ownerId.travelsInfo?.name || car.ownerId.name}</p>
                )}
              </div>
              {getStatusBadge(car)}
            </div>

            <div className="space-y-1 my-2">
              <div className="flex items-center text-xs text-gray-600">
                <Users className="h-3 w-3 mr-1.5" />
                {(car.seats || car.capacity) > 1 ? `${(car.seats || car.capacity) - 1}+1` : (car.seats || car.capacity)} Seater
              </div>
              <div className="flex items-center text-xs text-gray-600">
                <Fuel className="h-3 w-3 mr-1.5" />
                {car.fuelType}
              </div>
              <div className="flex items-center text-xs text-gray-600">
                <MapPin className="h-3 w-3 mr-1.5" />
                {typeof car.location === 'object'
                  ? `${car.location.city || ''}, ${car.location.state || ''}`
                  : car.location}
              </div>
            </div>

            <div className="border-t pt-2 flex items-center justify-between">
              <div className="text-base font-bold text-blue-600">
                {formatHeadlineRate(car)}
              </div>
              {!isAdmin && (
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <span className="text-[10px] text-gray-500">{car.isPublicListing ? 'Public' : 'Private'}</span>
                  <button
                    onClick={(e) => togglePublicListing(e, car._id, car.isPublicListing)}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                      car.isPublicListing ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                    title={car.isPublicListing ? 'Visible to customers on Browse page. Click to hide.' : 'Hidden from customers. Click to make visible on Browse page.'}
                  >
                    <span
                      className="inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform"
                      style={{ transform: car.isPublicListing ? 'translateX(17px)' : 'translateX(3px)' }}
                    />
                  </button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  const CarListView = () => (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs">Image</TableHead>
              <TableHead className="text-xs">Car</TableHead>
              <TableHead className="text-xs">Registration</TableHead>
              <TableHead className="text-xs">Capacity</TableHead>
              <TableHead className="text-xs">Fuel</TableHead>
              <TableHead className="text-xs">Location</TableHead>
              <TableHead className="text-xs">Price/Day</TableHead>
              <TableHead className="text-xs">Status</TableHead>
              <TableHead className="text-xs">Public Booking</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedCars.map((car) => (
              <TableRow
                key={car._id}
                className="cursor-pointer hover:bg-gray-50"
                onClick={() => navigate(`/cars/${car._id}`)}
              >
                <TableCell>
                  <div className="h-10 w-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded flex items-center justify-center overflow-hidden">
                    {car.images && car.images.length > 0 ? (
                      <img
                        src={car.images[0]}
                        alt={`${formatName(car.make)} ${formatName(car.model)}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Car className="h-5 w-5 text-blue-600" />
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-sm font-medium text-gray-900">
                    {formatName(car.make)} {formatName(car.model)}
                  </div>
                  <div className="text-xs text-gray-500">{car.year}</div>
                </TableCell>
                <TableCell className="text-xs">{car.registrationNumber}</TableCell>
                <TableCell className="text-xs">{(car.seats || car.capacity) > 1 ? `${(car.seats || car.capacity) - 1}+1` : (car.seats || car.capacity)} Seater</TableCell>
                <TableCell className="text-xs">{car.fuelType}</TableCell>
                <TableCell className="text-xs">
                  {typeof car.location === 'object'
                    ? `${car.location.city || ''}, ${car.location.state || ''}`
                    : car.location}
                </TableCell>
                <TableCell className="text-xs font-semibold">
                  {formatHeadlineRate(car)}
                </TableCell>
                <TableCell>{getStatusBadge(car)}</TableCell>
                <TableCell>
                  <div
                    className="flex flex-col items-center gap-1"
                    onClick={(e) => e.stopPropagation()}
                    title={car.isPublicListing
                      ? 'This vehicle is visible to customers on the Browse page. Click to hide.'
                      : 'This vehicle is hidden from customers. Click to show on the Browse page.'
                    }
                  >
                    <button
                      onClick={(e) => togglePublicListing(e, car._id, car.isPublicListing)}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                        car.isPublicListing ? 'bg-green-500' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className="inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform"
                        style={{ transform: car.isPublicListing ? 'translateX(17px)' : 'translateX(3px)' }}
                      />
                    </button>
                    <span className={`text-[10px] ${car.isPublicListing ? 'text-green-600' : 'text-gray-400'}`}>
                      {car.isPublicListing ? 'Visible' : 'Hidden'}
                    </span>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Vehicles"
        description={isAdmin ? "All vehicles across all owners" : "Manage your fleet of vehicles"}
        action={
          <div className="flex gap-2">
            <RefreshButton onClick={fetchCars} loading={loading} />
            {!isAdmin && cars.length > 0 && (
              businessType === 'both' ? (
                <div className="relative">
                  <Button
                    variant="outline"
                    onClick={() => setExportOpen((o) => !o)}
                    title="Download your vehicles as CSV (rental or sale)"
                  >
                    <Download className="h-5 w-5 mr-2" />
                    Export CSV
                  </Button>
                  {exportOpen && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setExportOpen(false)} />
                      <div className="absolute right-0 mt-1 z-20 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-1">
                        <p className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">Export which vehicles?</p>
                        <button onClick={() => handleExport('rental')} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                          <Car className="w-4 h-4 text-blue-600" /> Rental vehicles
                        </button>
                        <button onClick={() => handleExport('sale')} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                          <Tag className="w-4 h-4 text-emerald-600" /> Vehicles for sale
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <Button
                  variant="outline"
                  onClick={() => handleExport(businessType === 'sale' ? 'sale' : 'rental')}
                  title="Download your vehicles as a CSV file"
                >
                  <Download className="h-5 w-5 mr-2" />
                  Export CSV
                </Button>
              )
            )}
            {!isAdmin && (
              <Button
                variant="outline"
                onClick={() => setShowImport(true)}
                disabled={!canAddVehicle}
                title={addBlockedReason || 'Import vehicles from a CSV file'}
              >
                <Upload className="h-5 w-5 mr-2" />
                Import CSV
              </Button>
            )}
            {!isAdmin && (
              atPlanLimit ? (
                <Button variant="primary" onClick={() => navigate('/subscription')} title={addBlockedReason}>
                  <Sparkles className="h-5 w-5 mr-2" />
                  Upgrade to add more
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={() => navigate('/cars/add')}
                  disabled={!canAddVehicle}
                  title={addBlockedReason}
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Add Vehicle
                </Button>
              )
            )}
          </div>
        }
      />

      {/* Plan usage — owners only. The backend enforces the cap; this just explains it. */}
      {!isAdmin && planUsage && (
        <div className={`rounded-xl border px-4 py-3 flex flex-wrap items-center justify-between gap-3 ${
          atPlanLimit ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-gray-50'
        }`}>
          <div className="text-sm text-gray-700">
            <span className="font-semibold text-gray-900">{planUsage.plan}</span> plan ·{' '}
            <span className="font-semibold text-gray-900">{planUsed}</span>
            {planLimit === null ? ' vehicles (unlimited)' : ` / ${planLimit} vehicles used`}
            {hiddenCount > 0 && (
              <span className="ml-2 inline-flex items-center gap-1 text-amber-700 font-medium">
                <EyeOff className="h-3.5 w-3.5" />
                {hiddenCount} hidden from public listings
              </span>
            )}
          </div>
          {(atPlanLimit || hiddenCount > 0) && (
            <Button variant="outline" onClick={() => navigate('/subscription')}>
              <Sparkles className="h-4 w-4 mr-2" />
              Upgrade plan
            </Button>
          )}
        </div>
      )}

      {/* Verification Warning Banner */}
      {isOwner && !isVerified && (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start">
          <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div className="ml-3">
            <p className="text-sm text-yellow-800 font-medium">Account Verification Required</p>
            <p className="text-sm text-yellow-700">
              {verificationStatus === 'pending' && 'Your account is pending verification. You cannot add vehicles until your account is verified.'}
              {verificationStatus === 'rejected' && 'Your account verification was rejected. Please contact support.'}
              {verificationStatus === 'suspended' && 'Your account has been suspended. Please contact support.'}
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <Card className="mb-3">
        <CardContent className="p-3">
          <div className="flex flex-col gap-3">
            {/* Rental vs Buy/Sell tabs — shown when the fleet actually holds both
                kinds. A single-purpose fleet would just see duplicate tabs. */}
            {showPurposeTabs && (
              <div className="flex gap-1 p-1 bg-gray-100 rounded-xl">
                {[
                  { value: 'all', label: 'All Vehicles', count: cars.length, Icon: Car },
                  { value: 'rental', label: 'Rental', count: cars.filter(c => c.forRent !== false).length, Icon: Car },
                  { value: 'sale', label: 'For Sale', count: cars.filter(c => !!c.forSale).length, Icon: Tag },
                ].map(({ value, label, count, Icon }) => (
                  <button
                    key={value}
                    onClick={() => setPurposeFilter(value)}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-semibold transition-all ${
                      purposeFilter === value ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="hidden sm:inline">{label}</span>
                    <span className="sm:hidden">{value === 'all' ? 'All' : label}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                      purposeFilter === value ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-600'
                    }`}>{count}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <div className="flex-1">
                <Input
                  placeholder="Search by make, model, or registration..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full text-sm h-8"
                />
              </div>
              <div className="flex space-x-1">
                <Button
                  variant={viewMode === 'grid' ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('grid')}
                >
                  <Grid3x3 className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
            </div>
            {/* Owner Filter - Admin only */}
            {isAdmin && uniqueOwners.length > 0 && (
              <div className="flex items-center gap-2">
                <select
                  value={ownerFilter}
                  onChange={(e) => setOwnerFilter(e.target.value)}
                  className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Owners ({uniqueOwners.length})</option>
                  {uniqueOwners.map((owner) => (
                    <option key={owner.id} value={owner.id}>{owner.travelsName || owner.name}</option>
                  ))}
                </select>
                {ownerFilter !== 'all' && (
                  <button
                    onClick={() => setOwnerFilter('all')}
                    className="px-2 py-1.5 text-xs text-red-600 bg-red-50 hover:bg-red-100 rounded-lg font-medium"
                  >
                    Clear
                  </button>
                )}
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {[
                { value: 'all', label: 'All', color: 'bg-gray-100 text-gray-700 hover:bg-gray-200', activeColor: 'bg-gray-700 text-white' },
                { value: 'available', label: 'Available', color: 'bg-green-50 text-green-700 hover:bg-green-100', activeColor: 'bg-green-500 text-white' },
                { value: 'booked', label: 'Booked', color: 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100', activeColor: 'bg-yellow-500 text-white' },
                { value: 'maintenance', label: 'Maintenance', color: 'bg-orange-50 text-orange-700 hover:bg-orange-100', activeColor: 'bg-orange-500 text-white' },
                { value: 'inactive', label: 'Inactive', color: 'bg-red-50 text-red-700 hover:bg-red-100', activeColor: 'bg-red-500 text-white' },
              ].map((status) => (
                <button
                  key={status.value}
                  onClick={() => setStatusFilter(status.value)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
                    statusFilter === status.value ? status.activeColor + ' shadow-sm' : status.color
                  }`}
                >
                  {status.label}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cars List/Grid */}
      {loading ? (
        <div className="py-6">
          <CarLoader message="Loading your vehicles..." />
        </div>
      ) : filteredCars.length === 0 ? (
        <Card>
          <CardContent className="text-center py-6">
            <Car className="h-10 w-10 text-gray-400 mx-auto mb-2" />
            <p className="text-xs text-gray-500">No vehicles found</p>
          </CardContent>
        </Card>
      ) : viewMode === 'grid' ? (
        <>
          <CarGridView />
          <PaginationUI />
        </>
      ) : (
        <>
          <CarListView />
          <PaginationUI />
        </>
      )}

      {showImport && (
        <BulkImportModal
          onClose={() => setShowImport(false)}
          onImported={() => fetchCars()}
        />
      )}
    </div>
  );
};

export default CarList;
